import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Printer } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) =>
  new Date(`${m}-01T12:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' });

/**
 * Statement of Generosity — one-page printable year-end giving report.
 * Great for tax season AND for the family scrapbook.
 */
export default function GivingStatementPage() {
  const { year } = useParams();
  const [giving, setGiving] = useState(null);
  const [household, setHousehold] = useState(null);

  const load = useCallback(async () => {
    try {
      const [g, h] = await Promise.all([
        axios.get(`${API_URL}/api/budget/giving-summary/${year}`),
        axios.get(`${API_URL}/api/household`),
      ]);
      setGiving(g.data);
      setHousehold(h.data?.household || null);
    } catch { /* silent */ }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (!giving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-[#36454F]/50 font-['Manrope']">Loading statement…</p>
      </div>
    );
  }

  const months = Object.keys(giving.by_month || {}).sort();
  const householdName = household?.name || 'Household';

  return (
    <div className="print-root min-h-screen bg-[#F5F5F5] py-8" data-testid="giving-statement-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-root { background: #fff !important; padding: 0 !important; }
          .print-sheet { box-shadow: none !important; border: none !important; padding: 28pt !important; margin: 0 !important; max-width: none !important; }
          @page { margin: 0.5in; }
        }
      `}</style>
      <div className="no-print max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between">
        <Link to="/budget" className="text-sm font-['Manrope'] text-[#B76E79] hover:underline">← Back to Budget</Link>
        <Button
          onClick={() => window.print()}
          className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
          data-testid="statement-print-btn"
        >
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print / Save as PDF
        </Button>
      </div>

      <div
        className="print-sheet max-w-3xl mx-auto bg-white border border-[#36454F]/10 shadow-sm p-12 font-['Manrope']"
        data-testid="statement-sheet"
      >
        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-semibold">
            Selah Slate™ · Statement of Generosity
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold text-[#36454F] mt-2">
            {householdName}
          </h1>
          <p className="text-sm text-[#36454F]/60 mt-1">Giving for the year of our Lord {year}</p>
        </div>

        <div className="text-center border-y border-[#B76E79]/25 py-6 my-8">
          <p className="text-[11px] uppercase tracking-widest text-[#B76E79] font-semibold">
            Total given
          </p>
          <p
            className="font-['Playfair_Display'] text-6xl font-semibold text-[#B76E79] mt-2"
            data-testid="statement-total"
          >
            {fmt(giving.total)}
          </p>
        </div>

        {months.length > 0 ? (
          <table className="w-full text-sm border border-[#36454F]/10" data-testid="statement-monthly-table">
            <thead className="bg-[#F5F5F5] text-[#36454F]/60">
              <tr>
                <th className="text-left px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">Month</th>
                <th className="text-right px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">Given</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m} className="border-t border-[#36454F]/8">
                  <td className="px-4 py-3 text-[#36454F]">{monthLabel(m)}</td>
                  <td className="px-4 py-3 text-right text-[#36454F] font-semibold">{fmt(giving.by_month[m])}</td>
                </tr>
              ))}
              <tr className="border-t border-[#36454F]/15 bg-[#F5F5F5]">
                <td className="px-4 py-3 text-[#36454F] font-semibold uppercase text-[11px] tracking-wider">Total</td>
                <td className="px-4 py-3 text-right text-[#B76E79] font-semibold font-['Playfair_Display'] text-lg">{fmt(giving.total)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-center text-[#36454F]/50 italic py-6">No giving recorded yet for {year}.</p>
        )}

        <div className="mt-10 p-5 border-l-4 border-[#B76E79] bg-[#FFF5F1]">
          <p className="font-['Playfair_Display'] italic text-[#36454F] leading-relaxed text-[15px]">
            &ldquo;A generous person will prosper; whoever refreshes others will be refreshed.&rdquo;
          </p>
          <p className="text-xs text-[#B76E79] mt-2 font-semibold">Proverbs 11:25</p>
        </div>

        <p className="mt-10 text-[11px] text-[#36454F]/45 italic text-center leading-relaxed">
          This statement was generated by Selah Slate from your own household&apos;s ledger. It is a
          record of your Tithe &amp; Giving envelope activity — no one at Selah Slate reviewed the
          numbers, and no personal information left your account. Retain for your records.
        </p>
      </div>
    </div>
  );
}
